import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU, maskData } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Mail, Phone, Building, Briefcase, Users, Building2, 
  CheckCircle2, Calendar, Lock
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NotesBlock from '@/components/NotesBlock';
import CallLogCard from '@/components/CallLogCard';
import CallLogModal from '@/components/modals/CallLogModal';
import { toast } from 'sonner';

export default function AdviserProfile() {
  const { clientId, adviserId } = useParams();
  const { client } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [adviser, setAdviser] = useState(null);
  const [individuals, setIndividuals] = useState([]);
  const [entities, setEntities] = useState([]);
  const [allAdvisers, setAllAdvisers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCallLogModal, setShowCallLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !clientId || !adviserId) return;
      try {
        const [advRes, indRes, entRes, allAdvRes, callLogsRes] = await Promise.all([
          api.getAdviser(token, clientId, adviserId),
          api.getIndividuals(token, clientId),
          api.getEntities(token, clientId),
          api.getAdvisers(token, clientId),
          api.getCallLogs(token, clientId)
        ]);
        setAdviser(advRes.data);
        setIndividuals(indRes.data);
        setEntities(entRes.data);
        setAllAdvisers(allAdvRes.data);
        // Filter call logs for this adviser
        setCallLogs(callLogsRes.data.filter(log => 
          log.linkedAdviserId === adviserId || 
          log.participants?.some(p => p.id === adviserId)
        ));
      } catch (error) {
        console.error('Failed to fetch adviser:', error);
        navigate(`/app/clients/${clientId}/advisers`);
      }
      setLoading(false);
    };
    fetchData();
  }, [token, clientId, adviserId, navigate]);

  const handleSaveNotes = async (notes) => {
    try {
      const response = await api.updateAdviserNotes(token, clientId, adviserId, notes);
      setAdviser(response.data);
      toast.success('Notes saved');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const handleAddCallLog = async (callLogData) => {
    try {
      const response = await api.createCallLog(token, clientId, {
        ...callLogData,
        linkedAdviserId: adviserId
      });
      setCallLogs([response.data, ...callLogs]);
      toast.success('Call logged successfully');
    } catch (error) {
      console.error('Failed to create call log:', error);
      toast.error('Failed to log call');
    }
  };

  // Get individuals this adviser works with
  const worksWithIndividuals = individuals.filter(ind => 
    adviser?.worksWithIndividuals?.includes(ind.id)
  );

  // Get entities this adviser works with
  const worksWithEntities = entities.filter(ent => 
    adviser?.worksWithEntities?.includes(ent.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!adviser) {
    return (
      <div className="empty-state">
        <Briefcase className="w-12 h-12" />
        <p className="text-lg font-medium text-[#5D524D]">Adviser not found</p>
      </div>
    );
  }

  const isRestricted = client?.restricted;

  return (
    <div className="animate-fade-in-up" data-testid="adviser-profile">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(`/app/clients/${clientId}/advisers`)}
        className="caffeine-btn-ghost mb-4"
        data-testid="back-to-advisers-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Advisers
      </Button>

      {/* Profile Header */}
      <div className="caffeine-card p-6 mb-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-2xl font-medium">
            {adviser.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="font-heading text-2xl text-[#2C2420]">{adviser.name}</h1>
              {adviser.primary && (
                <span className="badge-verified">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Primary
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                adviser.type === 'Internal' ? 'bg-[#4A3B32]/10 text-[#4A3B32]' : 'bg-[#F0EBE6] text-[#5D524D]'
              }`}>
                {adviser.type}
              </span>
            </div>
            <p className="text-[#5D524D] mb-1">{adviser.roleCategory} • {adviser.department}</p>
            <p className="text-sm text-[#8C8078]">{adviser.organisation}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Details */}
        <div className="caffeine-card p-6">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">Contact Details</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Building className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Organisation</p>
                <p className="text-[#2C2420]">{adviser.organisation}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Department</p>
                <p className="text-[#2C2420]">{adviser.department}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Email</p>
                {isRestricted ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="masked-field">
                          <Lock className="w-3.5 h-3.5" />
                          {maskData(adviser.email, 'email')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="sensitive-tooltip">
                        Contact details are masked for restricted clients.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <p className="text-[#2C2420]">{adviser.email}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Phone</p>
                {isRestricted ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="masked-field">
                          <Lock className="w-3.5 h-3.5" />
                          {maskData(adviser.phone, 'phone')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="sensitive-tooltip">
                        Contact details are masked for restricted clients.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <p className="text-[#2C2420]">{adviser.phone}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Last Verified</p>
                <p className="text-[#2C2420]">{formatDateAU(adviser.lastVerified)}</p>
              </div>
            </div>
          </div>
          
          {adviser.notes && (
            <div className="mt-6 pt-4 border-t border-[#E6E0D9]">
              <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Notes</p>
              <p className="text-[#5D524D]">{adviser.notes}</p>
            </div>
          )}
        </div>

        {/* Works With */}
        <div className="space-y-6">
          {/* Individuals */}
          <div className="caffeine-card p-6">
            <h2 className="font-heading text-lg text-[#2C2420] mb-4">Works With (Individuals)</h2>
            {worksWithIndividuals.length > 0 ? (
              <div className="space-y-3">
                {worksWithIndividuals.map(ind => (
                  <div 
                    key={ind.id}
                    className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg cursor-pointer hover:bg-[#F0EBE6] transition-colors"
                    onClick={() => navigate(`/app/clients/${clientId}/individuals/${ind.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm">
                        {ind.firstName[0]}{ind.lastName[0]}
                      </div>
                      <div>
                        <p className="font-medium text-[#2C2420]">{ind.firstName} {ind.lastName}</p>
                        <p className="text-xs text-[#8C8078]">{ind.role}</p>
                      </div>
                    </div>
                    <Users className="w-4 h-4 text-[#8C8078]" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8C8078]">No individuals linked</p>
            )}
          </div>

          {/* Entities */}
          <div className="caffeine-card p-6">
            <h2 className="font-heading text-lg text-[#2C2420] mb-4">Works With (Entities)</h2>
            {worksWithEntities.length > 0 ? (
              <div className="space-y-3">
                {worksWithEntities.map(ent => (
                  <div 
                    key={ent.id}
                    className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg cursor-pointer hover:bg-[#F0EBE6] transition-colors"
                    onClick={() => navigate(`/app/clients/${clientId}/entities/${ent.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-[#4A3B32]" />
                      <div>
                        <p className="font-medium text-[#2C2420]">{ent.name}</p>
                        <p className="text-xs text-[#8C8078]">{ent.type}</p>
                      </div>
                    </div>
                    <Building2 className="w-4 h-4 text-[#8C8078]" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#8C8078]">No entities linked</p>
            )}
          </div>

          {/* Notes */}
          <NotesBlock
            notes={adviser?.notes}
            updatedAt={adviser?.notesUpdatedAt}
            updatedBy={adviser?.notesUpdatedBy}
            onSave={handleSaveNotes}
            placeholder={`Add notes about ${adviser?.name}...`}
          />

          {/* Call Log */}
          <CallLogCard
            callLogs={callLogs}
            onViewAll={() => navigate(`/app/clients/${clientId}/activity`)}
            onAddNew={() => setShowCallLogModal(true)}
            maxDisplay={3}
            title={`Call Log - ${adviser?.name}`}
          />
        </div>
      </div>

      {/* Call Log Modal */}
      <CallLogModal
        isOpen={showCallLogModal}
        onClose={() => setShowCallLogModal(false)}
        onSave={handleAddCallLog}
        individuals={individuals}
        advisers={allAdvisers}
      />
    </div>
  );
}
